import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import { API, ENTOURAGE_KEY } from "@/components/exo/driversExoShared";

export const DriversEntourageInvite = ({ token, onToken, onOpen }) => {
  const [lang, setLang] = useState("fr");
  const [creating, setCreating] = useState(false);
  const [proches, setProches] = useState([]);
  const [procheToken, setProcheToken] = useState("");

  useEffect(() => {
    if (!token) return undefined;
    let active = true;
    const poll = () => axios.get(`${API}/drivers-entourage/seances/${token}`).then((r) => { if (active && r.data.found) { setProches(Array.isArray(r.data.proches) ? r.data.proches : []); if (r.data.proche_token) setProcheToken(r.data.proche_token); } }).catch(() => {});
    poll();
    const id = setInterval(poll, 10000);
    return () => { active = false; clearInterval(id); };
  }, [token]);
  const shareUrl = procheToken ? `${window.location.origin}/p/drivers-entourage/${procheToken}` : "";

  const create = async () => {
    setCreating(true);
    try {
      const { token: newToken, proche_token: newProche } = await axios
        .post(`${API}/drivers-entourage/seances`, { patient_ranking: [], patient_entries: {}, lang })
        .then((r) => r.data);
      try { localStorage.setItem(ENTOURAGE_KEY, newToken); } catch { /* ignore */ }
      setProcheToken(newProche || "");
      onToken(newToken);
    } catch {
      toast.error("Impossible de créer le lien — réessayez.");
    }
    setCreating(false);
  };

  const copy = () => shareUrl && navigator.clipboard.writeText(shareUrl).then(() => toast.success("Lien copié")).catch(() => {});

  return (
    <div className="rounded-3xl bg-[var(--sand)] border border-[var(--border)] p-6 sm:p-8 mb-8 text-left no-print" data-testid="exo-entourage-invite">
      <div className="text-center mb-4">
        <h3 className="font-serif-title text-2xl sm:text-3xl text-[var(--gold)] mb-2">Le regard d'un proche</h3>
        <p className="text-sm text-[var(--ink)] leading-relaxed max-w-xl mx-auto">
          Partagez ce lien avec un proche. Il découvrira chaque driver et décrira comment il vous perçoit.
          Vous pouvez le créer dès maintenant ; la comparaison s'affichera dans votre synthèse une fois votre cahier terminé.
        </p>
      </div>

      {!token ? (
        <div className="text-center">
          <div className="inline-flex rounded-full border border-[var(--border)] bg-white/70 p-1 mb-5" data-testid="exo-entourage-lang-toggle">
            {[{ v: "fr", l: "Français" }, { v: "es", l: "Español" }].map((o) => (
              <button
                key={o.v}
                onClick={() => setLang(o.v)}
                data-testid={`exo-entourage-lang-${o.v}`}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${lang === o.v ? "bg-[var(--gold)] text-white" : "text-[var(--ink-soft)] hover:text-[var(--gold)]"}`}
              >
                {o.l}
              </button>
            ))}
          </div>
          <div>
            <button onClick={create} disabled={creating} data-testid="exo-entourage-create" className="px-8 py-3.5 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 transition-all disabled:opacity-60">
              {creating ? "Création…" : "Créer le lien à partager"}
            </button>
          </div>
        </div>
      ) : (
        <div className="text-center">
          {proches.length > 0 && (
            <div className="rounded-2xl bg-white/80 border border-[var(--gold-light)] p-4 mb-5" data-testid="exo-entourage-answered">
              <p className="text-sm text-[var(--ink)] mb-3" data-testid="exo-entourage-answered-count">
                <span className="font-semibold text-[var(--gold)]">{proches.length} proche{proches.length > 1 ? "s ont" : " a"} répondu</span>
                {proches.some((p) => p.name) ? ` (${proches.map((p) => p.name).filter(Boolean).join(", ")})` : ""}.
              </p>
              <button onClick={onOpen} data-testid="exo-entourage-open-from-welcome" className="px-6 py-3 rounded-full bg-[var(--ink)] text-white text-sm font-medium shadow-sm hover:brightness-110 transition-all">Voir les regards de mes proches</button>
            </div>
          )}
          <p className="text-xs text-[var(--ink-soft)] mb-2">Lien à partager avec votre proche</p>
          <div className="rounded-xl bg-white/80 border border-[var(--border)] p-3 text-sm text-[var(--ink)] break-all mb-4" data-testid="exo-entourage-link">{shareUrl || "Préparation du lien…"}</div>
          <div className="flex justify-center my-4">
            <div className="bg-white p-3 rounded-2xl border border-[var(--border)]">
              {shareUrl && <QRCodeSVG value={shareUrl} size={148} bgColor="#ffffff" fgColor="#4A3B36" level="M" />}
            </div>
          </div>
          <button onClick={copy} data-testid="exo-entourage-copy" className="px-8 py-3.5 rounded-full bg-[var(--gold)] text-white font-medium shadow-sm hover:brightness-105 transition-all">Copier le lien</button>
          <p className="text-xs text-[var(--ink-soft)] mt-4">
            Vous pouvez inviter plusieurs proches avec ce même lien. La comparaison apparaîtra dans votre synthèse une fois votre cahier terminé.
          </p>
        </div>
      )}
    </div>
  );
};
